import { useEffect, useRef, useState } from "react";
import { Clapperboard, Mic, FileText, Loader2 } from "lucide-react";
import { fileUrl } from "../lib/api";
import { CaptionBar, useCaptionTrack, useCurrentSegment } from "./MediaCaptions";
import { AddCaptions } from "./letter/AddCaptions";
import { LetterChapters } from "./letter/LetterChapters";
import { ShareLetter } from "./letter/ShareLetter";
import { LetterReplies } from "./letter/LetterReplies";

const fmt = (s) => (Number.isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "");

export const MediaPlayer = ({ doc, onUpdated, readOnly = false }) => {
  const type = doc?.content_type || "";
  const isVideo = type.startsWith("video/"), isAudio = type.startsWith("audio/");
  const ref = useRef(null);
  const [dur, setDur] = useState(null);
  const [err, setErr] = useState(false);
  const [cc, setCc] = useState(true);
  const segments = doc?.transcript_segments || [];
  const vtt = useCaptionTrack(segments);
  const current = useCurrentSegment(ref, segments);
  useEffect(() => { setDur(null); setErr(false); }, [doc?.id]);
  useEffect(() => {
    const track = ref.current?.textTracks?.[0];
    if (track) track.mode = cc ? "showing" : "hidden";
  }, [cc, vtt, err]);
  if (!doc?.storage_path || (!isVideo && !isAudio)) return null;
  const src = `${fileUrl(doc.storage_path)}?inline=1`;
  const Icon = isVideo ? Clapperboard : Mic;
  const seek = (t) => { if (ref.current) { ref.current.currentTime = t; ref.current.play?.().catch(() => {}); } };
  return (
    <section className="mt-6" data-testid="media-player">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2">
        <Icon className="h-3.5 w-3.5" /> {isVideo ? "Home video" : "Voice memo"}{dur ? ` · ${fmt(dur)}` : ""}
      </div>
      {err ? (
        <div className="rounded-xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground" data-testid="media-player-error">This browser can't play this format — use Open File to download it.</div>
      ) : isVideo ? (
        <video ref={ref} controls preload="metadata" playsInline src={src} poster={doc.thumbnail_path ? `${fileUrl(doc.thumbnail_path)}?inline=1` : undefined} onLoadedMetadata={(e) => setDur(e.currentTarget.duration)} onError={() => setErr(true)}
          className="w-full rounded-xl bg-black aspect-video" data-testid="media-player-video">
          {vtt && <track kind="captions" srcLang="en" label="Captions" src={vtt} default={cc} data-testid="media-player-track" />}
        </video>
      ) : (
        <div className="rounded-xl border border-border bg-muted/40 p-3">
          <audio ref={ref} controls preload="metadata" src={src} onLoadedMetadata={(e) => setDur(e.currentTarget.duration)} onError={() => setErr(true)} className="w-full" data-testid="media-player-audio" />
        </div>
      )}
      {!err && <CaptionBar segments={segments} current={current} on={cc} onToggle={() => setCc((v) => !v)} onSeek={seek} showLine={!isVideo} />}
      {!err && !readOnly && !segments.length && (doc.transcribed || doc.captioning || doc.caption_error) && <AddCaptions doc={doc} onUpdated={onUpdated} />}
      {!err && <LetterChapters doc={doc} mediaRef={ref} readOnly={readOnly} onUpdated={onUpdated} segments={segments} />}
      {!err && !readOnly && <div className="mt-3 flex justify-end"><ShareLetter doc={doc} /></div>}
      {!err && <LetterReplies doc={doc} readOnly={readOnly} />}
      {doc.transcribed && doc.text_content ? (
        <details className="mt-3 rounded-xl border border-border bg-muted/30 px-4 py-3" data-testid="media-transcript">
          <summary className="cursor-pointer text-sm font-medium flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-primary" /> Transcript <span className="text-xs text-muted-foreground font-normal">· searchable · Ask Your Vault can quote it</span></summary>
          <p className="mt-2 text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap max-h-64 overflow-y-auto" data-testid="media-transcript-text">{doc.text_content}</p>
        </details>
      ) : !err && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="media-transcript-pending"><Loader2 className="h-3 w-3 animate-spin" /> Transcript on its way — reopen in a minute.</div>
      )}
    </section>
  );
};
